import { useState } from 'react';

import { ModalWrapper, Typography, useDeviceType } from '@braskai/ui-kit';

import { Close } from '@mui/icons-material';
import { Box, IconButton, Modal, Stack } from '@mui/material';

import { useTrialPricingModalStore } from '@/entities/pricing';

import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PaymentInterval, Plan, Price } from '@/shared/types/Billing';

import { usePricingCardsController } from '../../model/usePricingCardsController';
import { BillingPeriodTabs } from '../BillingPeriodTabs';
import { ExpandedPlansFeatures } from '../ExpandedPlansFeatures';
import { PricingCard } from '../PricingCard';
import { EnterpriseBanner } from './EnterpriseBanner';

const SHEET_RADIUS = 20;
const SHEET_SIDE_GUTTER = { desktop: 80, mobile: 24 };
const SHEET_PADDING_X = { desktop: 24, mobile: 16 };
const CARD_MIN_HEIGHT = 400;

const TrialPricingModalComponent = () => {
  const close = useTrialPricingModalStore((state) => state.close);
  const { isMobile } = useDeviceType();
  const [chosenPlanId, setChosenPlanId] = useState<null | string>(null);

  const { paymentPeriod, plansGroupByCategory, yearlySelected, handleBuyPlanButtonClick, handleToggle } =
    usePricingCardsController({ defaultPaymentPeriod: PaymentInterval.Month, source: 'trialModal', setChosenPlanId });

  useExecuteOneTime(() => {
    MixpanelEvents.trialPricingModalShown();
  });

  const handleClose = () => {
    MixpanelEvents.trialPricingModalDismissed();
    close();
  };

  const handlePlanGetStarted = (priceId: Price['id'], plan: Plan) => {
    MixpanelEvents.trialPricingModalPlanGetStartedClick({ period: paymentPeriod, plan: plan.category });
    handleBuyPlanButtonClick(priceId, plan);
  };

  return (
    <Modal
      sx={{ alignItems: 'flex-end', display: 'flex', justifyContent: 'center' }}
      open
      onClose={handleClose}
    >
      <Stack
        sx={{
          backgroundColor: 'common.white',
          borderTopLeftRadius: SHEET_RADIUS,
          borderTopRightRadius: SHEET_RADIUS,
          maxHeight: 'calc(100dvh - 40px)',
          overflowY: 'auto',
          position: 'relative',
          width: `calc(100vw - ${isMobile ? SHEET_SIDE_GUTTER.mobile : SHEET_SIDE_GUTTER.desktop}px)`,
        }}
      >
        <Stack
          alignItems="center"
          gap={4}
          paddingBottom={25}
          paddingTop={24}
          paddingX={isMobile ? SHEET_PADDING_X.mobile : SHEET_PADDING_X.desktop}
        >
          <Typography color="grey.900" variant="title-4-md">
            Unlock full access
          </Typography>
          <BillingPeriodTabs yearlySelected={yearlySelected} onToggle={handleToggle} />
        </Stack>
        <IconButton
          aria-label="Close"
          sx={{ color: 'grey.500', position: 'absolute', right: 24, top: 24 }}
          onClick={handleClose}
        >
          <Close sx={{ fontSize: 24 }} />
        </IconButton>

        <Stack gap={20} paddingBottom={40} paddingX={isMobile ? SHEET_PADDING_X.mobile : SHEET_PADDING_X.desktop}>
          <Box
            sx={{
              '& > *': { minHeight: isMobile ? 'auto' : CARD_MIN_HEIGHT },
              display: 'grid',
              gap: 12,
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            }}
          >
            {plansGroupByCategory.map((plansByCategory) => (
              <PricingCard
                key={plansByCategory.category}
                chosenPlanId={chosenPlanId}
                plansByCategory={plansByCategory}
                selectedPaymentPeriod={paymentPeriod}
                onActionButtonClick={handlePlanGetStarted}
              />
            ))}
          </Box>

          <EnterpriseBanner />
        </Stack>

        <ExpandedPlansFeatures yearlySelected={yearlySelected} onToggle={handleToggle} />
      </Stack>
    </Modal>
  );
};

export const TrialPricingModal = () => (
  <ModalWrapper hook={useTrialPricingModalStore}>
    <TrialPricingModalComponent />
  </ModalWrapper>
);
